import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Observable, Subject } from 'rxjs'
export { withErrorBoundary } from 'react-error-boundary'

export const useInterval = (callback: () => void, delay: number) => {
  const savedCallback = useRef(() => {})

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current()
    }
    if (delay !== null) {
      const id = setInterval(tick, delay)
      return () => clearInterval(id)
    }
  }, [delay])
}

export const useQuery = () => {
  const location = useLocation()
  return new URLSearchParams(location.search)
}

export const useElementSize = () => {
  const ref = useRef<any>(null)
  const width = ref.current?.clientWidth || 0
  const height = ref.current?.clientHeight || 0

  return [ref, width, height]
}

// Set the value of a state variable based on the emitted value from an observable
export const useObservable = <T>(observableFactory: () => Observable<T>, initialValue: T, dependencies: any[]) => {
  // const [subs, setSubs] = useState<Subscription[]>([])
  const [value, setValue] = useState(initialValue)

  useEffect(() => {
    const obs = observableFactory()
    const activeSubscription = obs.subscribe(val => setValue(val))
    return () => {
      activeSubscription.unsubscribe()
    }
  }, dependencies)

  return value
}

// Allows to to set an observable that is subscribed to, and then updates a state value
export const useDynamicObservable = <T = any>(initialValue: T): [T, (obs: Observable<T>) => void] => {
  const [value, setValue] = useState(initialValue)
  const [obs, setObs] = useState<Observable<T>|null>(null)

  useEffect(() => {
    if (obs) {
      const sub = obs.subscribe(val => setValue(val))
      return () => sub.unsubscribe()
    }
  }, [obs])

  return [value, setObs]
}

export const useSubject = <T>(initialValue: T, dependencies: any[]): [T, Subject<T>] => {
  const [subject$] = useState(() => new Subject<T>())
  const [value, setValue] = useState(initialValue)

  useEffect(() => {
    const sub = subject$.subscribe(val => setValue(val))
    return () => sub.unsubscribe()
  }, [subject$, ...dependencies])

  return [value, subject$]
}
